import { lazy, type ComponentType, type LazyExoticComponent } from 'react'

interface LazyModule<T extends ComponentType> {
  default: T
}

const dynamicImportErrorPatterns = [
  'Failed to fetch dynamically imported module',
  'Importing a module script failed',
  'Loading chunk',
  'ChunkLoadError',
]

export function lazyWithReload<T extends ComponentType>(
  importer: () => Promise<LazyModule<T>>,
  moduleKey: string,
): LazyExoticComponent<T> {
  return lazy(async () => {
    const storageKey = `gleam:lazy-retry:${moduleKey}`

    try {
      const module = await importer()

      if (typeof window !== 'undefined') {
        window.sessionStorage.removeItem(storageKey)
      }

      return module
    } catch (error) {
      if (shouldReloadForDynamicImportError(error) && typeof window !== 'undefined') {
        const alreadyRetried = window.sessionStorage.getItem(storageKey) === '1'

        if (!alreadyRetried) {
          window.sessionStorage.setItem(storageKey, '1')
          window.location.reload()
          return new Promise<never>(() => undefined)
        }

        window.sessionStorage.removeItem(storageKey)
      }

      throw error
    }
  })
}

export function shouldReloadForDynamicImportError(error: unknown) {
  if (!(error instanceof Error)) {
    return false
  }

  return dynamicImportErrorPatterns.some((pattern) => error.message.includes(pattern))
}
